import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatFormField } from '@angular/material/form-field';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { CountryCode } from 'libphonenumber-js';
import { AccountService } from '../../../../../../../shared/account/account.service';
import { Logger } from '../../../../../../../infrastructure/logging/logger';
import { OrganizationService } from '../../../../../services/organizations.service';
import { FormFieldErrorComponent } from '../../../../../../../shared/components/form-field-error/form-field-error.component';
import { formatPhoneNumber } from '../../../../../../../utilities/PhoneUtility';
import { UserContextProvider } from '../../../../../../../infrastructure/context/user-context-provider';

export interface OrgMembersInviteDialogData {
  accountId?: number;
  firstName: string;
  lastName: string;
  emailAddress: string;
  phoneNumber: string;
  role: any;
  activationLink: boolean;
  password?: string;
  confirmPassword?: string;
  organizationId: string;
}

@Component({
  selector: 'app-org-members-invite-dialog',
  templateUrl: './org-members-invite-dialog.component.html',
  styleUrls: ['./org-members-invite-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormField,
    FormFieldErrorComponent,
    MatInputModule,
    MatSelectModule,
    MatCheckbox,
    MatDialogModule,
    MatButtonModule
  ]
})
export class OrgMembersInviteDialogComponent implements OnInit {

  formControl!: FormGroup;
  passwordFormControl!: FormGroup;
  dataSource!: MatTableDataSource<any>;
  matcher = new ErrorStateMatcher();
  created_account_id: number = 0;

  constructor(
    public dialogRef: MatDialogRef<OrgMembersInviteDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: OrgMembersInviteDialogData,
    private formBuilder: FormBuilder,
    private accountService: AccountService,
    private userContextProvider: UserContextProvider,
    private organizationService: OrganizationService,
    private logService: Logger
  ) { }

  ngOnInit(): void {
    this.formControl = this.formBuilder.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      emailAddress: ['', Validators.required],
      phoneNumber: ['', Validators.pattern(/^[\s()+-]*([0-9][\s()+-]*){10,11}(?:[\-\.\ \\\/]?(?:#|ext\.?|extension|x)[\-\.\ \\\/]?(\d+))?$/i)],
      role: ['', Validators.required]
    });

    this.passwordFormControl = this.formBuilder.group({
      password: ['', Validators.required],
      confirmPassword: ['', Validators.required]
    });
  }

  onActivationLinkToggle(event: any) {
    this.data.activationLink = event.checked;
  }

  isSaveEnabled(): boolean {
    if (this.data.activationLink) {
      return this.formControl.valid;
    }

    const passwordsMatch = this.passwordFormControl.get('password')?.value == this.passwordFormControl.get('confirmPassword')?.value;
    return this.formControl.valid && (this.passwordFormControl.valid && passwordsMatch);
  }
    
  async onSaveClick() {
    try {
      this.updateData();
      await this.sendActivationEmail();
      this.data.accountId = this.created_account_id;
      this.dialogRef.close(this.data);
    } catch (e) {
      let message;
      if (e instanceof Error) {
        message = e.message;
      } else {
        message = String(e);
      }
      this.logService.handleError(e, 'An error occurred while submitting Member', 'An error occurred while submitting Member');
    }
  }

  updateData() {
    this.data.firstName = this.formControl.controls['firstName'].value;
    this.data.lastName = this.formControl.controls['lastName'].value;
    this.data.emailAddress = this.formControl.controls['emailAddress'].value;
    this.data.phoneNumber = this.formControl.controls['phoneNumber'].value;
    this.data.role = this.formControl.controls['role'].value;

    this.data.password = this.passwordFormControl.controls['password'].value;
    this.data.confirmPassword = this.passwordFormControl.controls['confirmPassword'].value;
  }

  async sendActivationEmail() {
    await this.sendAccountCreateRequest();
  }

  onPhoneNumberChanged(event: Event) {
    this.updatePhoneNumber((event.target as HTMLInputElement).value);
  }

  updatePhoneNumber(phoneNumber: string) {
    const formattedPhoneNumber = formatPhoneNumber(phoneNumber, this.userContextProvider.countryCode as CountryCode);
    this.formControl.patchValue({ phoneNumber: formattedPhoneNumber });
  }

  async validateEmailAddress() {
    const response = await this.accountService.validateEmail(this.data.emailAddress);
    return !response.existing;
  }

  async sendAccountCreateRequest() {
    try {
      const payload: any = {
        firstName: this.data.firstName, 
        lastName: this.data.lastName, 
        organizationId: this.data.organizationId,
        emailAddress: this.data.emailAddress,
        phoneNumber: this.data.phoneNumber,
        roleId: this.data.role
      };
  
      if (this.data.activationLink) {
        payload['requireActivation'] = true;
      } else {
        payload['password'] = this.data.password;
        payload['confirmPassword'] = this.data.confirmPassword;
        payload['requireActivation'] = false;
      }
      this.created_account_id = (await this.organizationService.createAccount(payload)).data.account_id;
    } catch (e) {
      if (e instanceof Error) {
        this.logService.handleError(e, 'An error occurred while creating Member Account', 'An error occurred while creating Member Account');
      }
    }
  }

  onCloseClick(): void {
    this.dialogRef.close();
  }
}